/**
 * NETWORK ACCESS (IAM-TO-RBAC §6) — who may CHANGE this network: everyone in the workspace who can build (default), or
 * only chosen people (plus its creator and workspace admins). Everyone still sees it and uses its rooms. Only the
 * creator or a workspace admin changes this (``network.canChangeAccess``); the server enforces it either way.
 */
import React, { useEffect, useState } from 'react';
import MemberSearch from '@stepflow/ui-shared/components/workspace/MemberSearch.jsx';
import { getRestApiBearerToken } from '@/services/apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

const emailsOf = (access) => ((access && access.editors) || []).map((e) => e.email).filter(Boolean);

export default function NetworkAccessSection({ network, tenantId, onNetworkUpdate }) {
  const saved = network?.access || { mode: 'workspace', editors: [] };
  const [mode, setMode] = useState(saved.mode || 'workspace');
  const [emails, setEmails] = useState(emailsOf(saved));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const canChange = !!network?.canChangeAccess;

  useEffect(() => { setMode(saved.mode || 'workspace'); setEmails(emailsOf(saved)); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [network?.networkId, saved.mode, JSON.stringify(saved.editors || [])]);

  const dirty = mode !== (saved.mode || 'workspace')
    || (mode === 'restricted' && JSON.stringify([...emails].sort()) !== JSON.stringify([...emailsOf(saved)].sort()));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(network.networkId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ access: { mode, editors: mode === 'restricted' ? emails.map((email) => ({ email })) : [] } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      onNetworkUpdate?.({ ...network, access: body.access });
      setMsg({ ok: true, text: 'Saved' });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const radio = (value, label, help) => (
    <label className={`flex items-start gap-2 ${canChange ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
      <input type="radio" name="network-access" value={value} checked={mode === value} disabled={!canChange}
        onChange={() => { setMode(value); setMsg(null); }} data-testid={`network-access-${value}`} className="mt-0.5" />
      <span>
        <span className="block text-sm text-gray-800">{label}</span>
        <span className="block text-xs text-gray-500">{help}</span>
      </span>
    </label>
  );

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4" data-testid="network-access">
      <h3 className="text-sm font-semibold text-gray-800">Who can change this network</h3>
      <div className="space-y-2">
        {radio('workspace', 'Everyone in the workspace who can build', 'The default — anyone with build access can change it.')}
        {radio('restricted', 'Only the people I choose',
          'Plus its creator and workspace admins. Everyone else can still see it and use its rooms.')}
      </div>
      {mode === 'restricted' ? (
        <MemberSearch tenantId={tenantId} mode="multi" value={emails} disabled={!canChange}
          onChange={(v) => { setEmails(v); setMsg(null); }} ariaLabel="Network editors" testId="network-editors"
          placeholder="Add people by name or email…" />
      ) : null}
      {canChange ? (
        <div className="flex items-center gap-3">
          <button type="button" onClick={save} disabled={busy || !dirty} data-testid="network-access-save"
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save access'}
          </button>
          {msg ? <span role="status" className={`text-xs ${msg.ok ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</span> : null}
        </div>
      ) : (
        <p className="text-xs text-gray-500">Only this network's creator or a workspace admin can change this.</p>
      )}
    </section>
  );
}
